const PRODUCT_TYPES = new Set(["drug", "biologic", "vaccine", "otc", "device", "pipeline"]);
const FAMILY_TYPES = new Set(["drug", "biologic", "vaccine", "otc", "device"]);

const scalarValue = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

const firstValue = (...values) => values.map(scalarValue).find(Boolean) || "";

// Match the existing manufacturer-name normalization used by the dropdowns.
const normalizeName = (value) => scalarValue(value)
  .toLowerCase()
  .split(/[\s,;:"'()\[\]]+/)
  .map((token) => token.replace(/^\.+|\.+$/g, ""))
  .filter(Boolean)
  .join(" ");

const normalizeType = (value) => {
  const type = scalarValue(value).toLowerCase().replace(/\s+/g, " ");
  return type === "disease" ? "diseases" : type;
};

const part = (value) => `${value.length}:${value}`;
// The server's family key distinguishes ordinary Drugs from verified BLA
// copies. Its representative can retain either real source category.
const familyNamespace = (type) => type === "drug" || type === "biologic" ? "medication" : type;
// Match helper/productFamily.ts exactly. Ingredient commas are part of a name,
// and internal whitespace or Unicode variants must not create broader families.
const familyNames = (value) => [...new Set((Array.isArray(value) ? value : [value])
  .map((name) => scalarValue(name).toLowerCase()).filter(Boolean))].sort().map(part).join("");

const familyIdentity = (selected, type, isHistory) => {
  if (!FAMILY_TYPES.has(type)) return null;
  const suppliedFamily = scalarValue(selected?.catalogDisplayFamily);
  if (suppliedFamily) return JSON.stringify([familyNamespace(type), "family", suppliedFamily]);
  if (!isHistory) return null;
  // Older click rows can contain complete scalar names or complete name arrays.
  // Never infer a family from the manufacturer, alias, or just one of the sets.
  const brand = familyNames(selected?.productName);
  const generic = familyNames(selected?.genericName);
  // Without authoritative catalog metadata the original category stays in the
  // key. Stored names alone never establish a verified Drug/Biologic match.
  return brand && generic ? JSON.stringify([familyNamespace(type), "family", [type, brand, generic].map(part).join("")]) : null;
};

const recordIdentity = (row, kind, type) => {
  const id = kind === "history"
    ? firstValue(row?.clickedProduct?.productId)
    : firstValue(row?.id, row?._id);
  return id ? JSON.stringify([type, "id", id]) : null;
};

/**
 * Identify a display family, or the selected record when no family is known.
 * History row IDs identify the history event, so only clickedProduct IDs count.
 * A null identity leaves an incomplete row visible instead of merging it with
 * another row that also has no identity.
 */
export const getSearchSuggestionIdentity = (row, kind = "search") => {
  const isHistory = kind === "history";
  const selected = isHistory ? row?.clickedProduct : row;
  const type = normalizeType(firstValue(row?.type, selected?.type, selected?.productCategory));

  if (type === "manufacturer") {
    const name = normalizeName(isHistory
      ? firstValue(selected?.productName, selected?.name, row?.name)
      : firstValue(row?.name, row?.term));
    return name ? JSON.stringify([type, "name", name]) : null;
  }

  const family = familyIdentity(selected, type, isHistory);
  if (family) return family;
  const record = recordIdentity(row, kind, type);
  if (record) return record;

  if (PRODUCT_TYPES.has(type)) {
    const alias = firstValue(selected?.productAlias, selected?.slug);
    return alias ? JSON.stringify([type, "alias", alias]) : null;
  }

  // displayTerm may be a matched synonym; prefer the canonical term instead.
  const term = normalizeName(isHistory
    ? firstValue(selected?.term, selected?.productName, row?.term, row?.name)
    : firstValue(row?.term, row?.canonicalTerm, row?.name));
  return term ? JSON.stringify([type, "term", term]) : null;
};

/** Also match an incomplete history row to its exact selected fresh record. */
export const getSearchSuggestionIdentities = (row, kind = "search") => {
  const primary = getSearchSuggestionIdentity(row, kind);
  const selected = kind === "history" ? row?.clickedProduct : row;
  const type = normalizeType(firstValue(row?.type, selected?.type, selected?.productCategory));
  const record = FAMILY_TYPES.has(type) ? recordIdentity(row, kind, type) : null;
  return [...new Set([primary, record].filter(Boolean))];
};

/** Borrow family presentation without changing the historical selected record. */
export const withSearchSuggestionHistoryDisplay = (history, suggestions) => {
  const historyIdentities = new Set(getSearchSuggestionIdentities(history, "history"));
  const suggestion = (Array.isArray(suggestions) ? suggestions : []).find((row) =>
    FAMILY_TYPES.has(normalizeType(row?.type)) && scalarValue(row?.catalogDisplayFamily)
    && getSearchSuggestionIdentities(row).some((identity) => historyIdentities.has(identity)));
  if (!suggestion || !history?.clickedProduct) return history;
  const details = suggestion.displayDetails || {};
  return {
    ...history,
    clickedProduct: {
      ...history.clickedProduct,
      catalogDisplayFamily: suggestion.catalogDisplayFamily,
      familyRecordCount: suggestion.familyRecordCount,
      displayDetails: {
        ...history.clickedProduct.displayDetails,
        ...(details.manufacturers && { manufacturers: details.manufacturers }),
        genericNames: details.genericNames || suggestion.openfda?.generic_name,
      },
    },
  };
};

/** Keep the first occurrence of a known family/record, preserving incomplete rows. */
export const uniqueSearchSuggestions = (rows, kind = "search") => {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const identities = getSearchSuggestionIdentities(row, kind);
    if (identities.some((identity) => seen.has(identity))) return false;
    identities.forEach((identity) => seen.add(identity));
    return true;
  });
};
